import type { NavConfig } from '@pressreader/navigation-types';
import { readQueryValue } from '@pressreader/routing';

/** The reader's query parameter: the article a context URL is showing — `<context URL>?aid=<articleId>`. */
export const READER_ARTICLE_ID_PARAM = 'aid';

/** The pre-`aid` spelling of the same parameter, kept for entries and legacy-owned contexts that still write it. */
export const LEGACY_READER_ARTICLE_ID_PARAM = 'popupArticleId';

export interface NavConfigWithReaderArticleId extends NavConfig {
    aid?: string;
    popupArticleId?: string;
}

/**
 * The config fields that make an entry OPEN a reader article: both url spellings of the article id, the legacy
 * spelling's nested metadata, and the entry's own reader record. They describe the article shown OVER a context,
 * never the context itself; the presentation fields an Article entry carries (`articleId`, `lang`, `feedCursor`,
 * `context`) are deliberately not among them — contexts own fields of the same names (a feed's `lang`, a landing's
 * `articleId`). The lazy `stripReaderParams` removes exactly these; the eager finalizer stub removes them when the
 * reader chunk cannot be loaded.
 */
export const READER_OPENING_MARKERS = [READER_ARTICLE_ID_PARAM, LEGACY_READER_ARTICLE_ID_PARAM, 'popupArticleConfig', 'readerEntry'] as const;

/** One of the reader's opening markers. */
export type ReaderOpeningMarker = typeof READER_OPENING_MARKERS[number];

/**
 * The article a config carries as the reader's, or `null` for a config that carries none. `aid` wins
 * over the legacy alias when a config somehow carries both.
 *
 * A config answers what is being READ, not what the entry IS: a config restored from history state
 * outlives the URL that wrote it, so the entry's own identity comes from its URL — see
 * `readReaderArticleIdFromSearch`.
 */
export function getReaderArticleId(config: NavConfig): string | null {
    const { aid, popupArticleId } = config as NavConfigWithReaderArticleId;

    return aid || popupArticleId || null;
}

/**
 * The article a URL's query names as the reader's, or `null` when it names none — the identity of the
 * entry that URL belongs to.
 *
 * The query is read with the routing layer's own reader, so the id read here is the one route
 * resolution puts on the config: `aid` wins over the legacy alias, the last occurrence of a name wins,
 * a name carrying no value counts as none, and the value is decoded the way a declared parameter is.
 */
export function readReaderArticleIdFromSearch(search: string): string | null {
    return readQueryValue(search, [READER_ARTICLE_ID_PARAM, LEGACY_READER_ARTICLE_ID_PARAM]) ?? null;
}
